/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { SourcesByResolution, VideoPlayerHandle } from '@coursera-web/video-player';

import Imgix from 'js/components/Imgix';

import { Skeleton, useReducedMotion } from '@coursera/cds-core';

import type { MediaAsset } from 'bundles/premium-hub/types';

// VideoPlayer pulls in the video.js runtime (~140kB before gzip). Defer it so it
// stays out of the premium-hub bundle until this component actually renders a video.
const VideoPlayer = lazy(() => import('bundles/premium-hub/components/shared/VideoPlayer'));

export type VideoSettings = {
  // Ignored when the user prefers reduced motion.
  autoplay?: boolean;
  muted?: boolean;
  // Label that groups this video's player errors in Sentry.
  sentryErrorSource?: string;
};

export type MediaProps = {
  heading: string;
  media?: MediaAsset;
  videoSettings?: VideoSettings;
  // Prefix for the `data-testid`s: `${dataTestIdPrefix}-video` and `${dataTestIdPrefix}-image`.
  dataTestIdPrefix?: string;
};

const styles = {
  root: css`
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: var(--cds-border-radius-100);
  `,
  image: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  `,
  player: css`
    --video-player-border-radius: var(--cds-border-radius-100);

    border: none;
  `,
};

const Media = ({ heading, media, videoSettings, dataTestIdPrefix }: MediaProps) => {
  const { autoplay = false, muted = false, sentryErrorSource } = videoSettings ?? {};
  const isVideo = media?.contentType?.startsWith('video/') ?? false;

  // useReducedMotion returns false until its effect reads the real preference, so hold
  // autoplay back until after the first effects have run.
  const prefersReducedMotion = useReducedMotion();
  const [isPreferenceChecked, setIsPreferenceChecked] = useState(false);
  useEffect(() => setIsPreferenceChecked(true), []);

  // The browser decides on native autoplay as soon as the video has enough data, which can be before
  // the player has muted it. Start playback ourselves once the video's metadata is loaded and muted.
  const shouldAutoplay = useMemo(
    () => autoplay && !prefersReducedMotion && isPreferenceChecked,
    [autoplay, prefersReducedMotion, isPreferenceChecked]
  );
  const playerRef = useRef<VideoPlayerHandle>(null);

  const playIfAutoplay = useCallback<() => void>(() => {
    if (shouldAutoplay) {
      playerRef.current?.play();
    }
  }, [shouldAutoplay]);

  // Covers the preference check finishing after the video's metadata already loaded.
  useEffect(() => {
    if (shouldAutoplay && playerRef.current?.duration() !== undefined) {
      playIfAutoplay();
    }
  }, [shouldAutoplay, playIfAutoplay]);

  const sources = useMemo<SourcesByResolution | undefined>(
    () => (isVideo && media?.url ? { '720p': { [media.contentType as string]: media.url } } : undefined),
    [isVideo, media?.contentType, media?.url]
  );

  if (isVideo && sources) {
    return (
      <div css={styles.root} data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-media-video` : undefined}>
        <Suspense fallback={<Skeleton variant="rect" height="100%" width="100%" />}>
          <VideoPlayer
            ref={playerRef}
            onLoadedMetadata={playIfAutoplay}
            css={styles.player}
            sources={sources}
            layoutMode="fill"
            autoplay={autoplay && !prefersReducedMotion}
            deferAutoplay={!isPreferenceChecked}
            muted={muted}
            crossOrigin="anonymous"
            disableGlobalHotkeys
            name={heading}
            ariaLabel={media?.description ?? undefined}
            sentryErrorSource={sentryErrorSource}
          />
        </Suspense>
      </div>
    );
  }

  if (media?.url) {
    return (
      <div css={styles.root} data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-media-image` : undefined}>
        <Imgix
          src={media.url}
          alt={media.description ?? ''}
          css={styles.image}
          loading="lazy"
          imgParams={{ auto: 'format,compress', fit: 'fill' }}
        />
      </div>
    );
  }

  return null;
};

export default Media;
